import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Radio as RadioIcon, PlayCircle, Sparkles, ChevronRight, TrendingUp, Music2, Search, Star } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useRadioPlayer } from "../context/RadioPlayerContext";
import { useAuth } from "../context/AuthContext";
import { TAPIN_RADIO_LOGO } from "../lib/radioBrand";
import PageHeroBanner from "../components/PageHeroBanner";
import { RADIO_HOTSPOTS } from "../components/heroBannerHotspots";
import LiveNowBadge from "../components/LiveNowBadge";

/**
 * Radio — Tap IN's music network home.
 *
 *  • Featured hero (today's curated playlist)
 *  • Top 30 rail (global most-played)
 *  • 8 station cards (R&B Love Songs, Hip Hop, Gospel, Latin, Country, Rock, Jazz, Kids)
 *  • Featured playlists rail (admin-curated "Top Picks" etc.)
 *
 * Every track click bubbles up to the global <RadioPlayer /> docked at the
 * bottom of the app (see `RadioPlayerContext`).
 */
export default function Radio() {
  const [stations, setStations] = useState([]);
  const [top30, setTop30] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  // Map of station_slug → live host session, populated from /api/radio/live-now.
  // Surfaced as an "ON AIR" pill on each station card so listeners can
  // see at a glance which stations have a real human spinning right now.
  const [liveBySlug, setLiveBySlug] = useState({});
  // User's favourited stations — drives the "⭐ Your Stations" rail at the top.
  const [favouriteSlugs, setFavouriteSlugs] = useState(() => new Set());
  // Plain-text search + category filter applied client-side over the
  // already-loaded station list. Cheap, instant, no extra API calls.
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const { playQueue } = useRadioPlayer();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [hero, setHero] = useState(null);

  useEffect(() => {
    api.get("/radio/stations").then((r) => setStations(r.data || [])).catch(() => setStations([]));
    api.get("/radio/top30").then((r) => setTop30(r.data || [])).catch(() => setTop30([]));
    // Live-now poll — 20s refresh keeps the badge fresh without hammering the API.
    const fetchLive = async () => {
      try {
        const { data } = await api.get("/radio/live-now");
        setLiveBySlug(data?.by_slug || {});
      } catch { setLiveBySlug({}); }
    };
    fetchLive();
    const id = setInterval(fetchLive, 20000);
    // Hydrate user's favourites once.
    if (user) {
      api.get("/radio/favourites").then((r) => {
        const slugs = (r.data?.items || []).map((s) => s.slug);
        setFavouriteSlugs(new Set(slugs));
      }).catch(() => {});
    }
    api.get("/radio/playlists").then(async (r) => {
      const list = r.data || [];
      setPlaylists(list);
      // Load the hero playlist's tracks so "Play Now" can immediately enqueue
      // without a round-trip — otherwise the button does nothing on first click.
      if (list[0]?.slug) {
        try {
          const { data } = await api.get(`/radio/playlists/${list[0].slug}`);
          setHero(data);
        } catch { setHero(list[0]); }
      }
    }).catch(() => setPlaylists([]));
    return () => clearInterval(id);
  }, []);

  // Categories list — derived from the loaded stations so it auto-grows
  // as admins add new station genres without code changes.
  const categories = useMemo(() => {
    const cats = new Set(stations.map((s) => s.category).filter(Boolean));
    return ["all", ...Array.from(cats).sort()];
  }, [stations]);

  // Filtered station list applied to BOTH the favourites rail and main grid.
  const filteredStations = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return stations.filter((s) => {
      if (category !== "all" && (s.category || "") !== category) return false;
      if (!needle) return true;
      return (
        (s.name || "").toLowerCase().includes(needle) ||
        (s.description || "").toLowerCase().includes(needle)
      );
    });
  }, [stations, q, category]);

  const favouriteStations = useMemo(
    () => filteredStations.filter((s) => favouriteSlugs.has(s.slug)),
    [filteredStations, favouriteSlugs]
  );

  // Toggle favourite. Optimistic update; if the API errors we revert.
  const toggleFavourite = async (slug) => {
    if (!user) {
      toast.error("Sign in to favourite stations");
      navigate("/login");
      return;
    }
    const next = new Set(favouriteSlugs);
    const was = next.has(slug);
    if (was) next.delete(slug); else next.add(slug);
    setFavouriteSlugs(next);
    try {
      if (was) await api.delete(`/radio/station/${slug}/favourite`);
      else await api.post(`/radio/station/${slug}/favourite`);
    } catch {
      setFavouriteSlugs((cur) => {
        const r = new Set(cur);
        if (was) r.add(slug); else r.delete(slug);
        return r;
      });
      toast.error("Could not update favourite");
    }
  };

  return (
    <div data-testid="radio-page" className="min-h-screen bg-[#05060a] text-white pb-[160px]">
      <PageHeroBanner
        src="/tapin-radio-hero.png"
        alt="Tap IN Radio — Real People. Real Conversations. Real Time."
        testid="radio-page-hero"
        hotspots={RADIO_HOTSPOTS}
      />
      {/* HERO — featured playlist */}
      {hero && (
        <section
          data-testid="radio-hero"
          className="relative overflow-hidden"
          style={{
            minHeight: 360,
            backgroundImage: `linear-gradient(to right, rgba(5,6,10,0.92), rgba(5,6,10,0.2)), url(${hero.cover_url || ""})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-6 pb-14 relative z-10 flex items-start gap-6 flex-wrap">
            <img
              src={TAPIN_RADIO_LOGO}
              alt="Tap IN Radio"
              data-testid="tapin-radio-hero-logo"
              className="w-24 h-24 sm:w-32 sm:h-32 object-contain shrink-0 drop-shadow-[0_4px_28px_rgba(220,38,38,0.45)]"
            />
            <div className="min-w-0 flex-1">
            <div className="text-red-400 uppercase tracking-[0.3em] text-[11px] font-black mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> TAP IN RADIO · Featured Playlist
            </div>
            <h1 className="font-display text-5xl sm:text-6xl font-black leading-[1.02] mb-2">{hero.name}</h1>
            <p className="text-white/80 text-base max-w-[640px] mb-6">{hero.description}</p>
            <div className="flex items-center gap-3">
              <button
                data-testid="radio-hero-play"
                onClick={() => {
                  if (hero.tracks?.length) { playQueue(hero.tracks, 0, { source: `playlist:${hero.slug}` }); }
                  else navigate(`/radio/playlist/${hero.slug}`);
                }}
                className="inline-flex items-center gap-2 bg-tv-accent hover:bg-yellow-400 text-black font-display font-black text-lg px-6 py-3 rounded-full"
              >
                <PlayCircle className="w-6 h-6" /> Play Now
              </button>
              <Link
                data-testid="radio-hero-open"
                to={`/radio/playlist/${hero.slug}`}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold px-5 py-3 rounded-full border border-white/10"
              >
                Open Playlist <ChevronRight className="w-4 h-4" />
              </Link>
              <Link
                data-testid="radio-host-cta"
                to="/radio/host"
                className="inline-flex items-center gap-2 bg-fuchsia-500/20 hover:bg-fuchsia-500/35 text-fuchsia-100 font-bold px-5 py-3 rounded-full border border-fuchsia-500/40"
                title="Apply to host your own show on Tap IN Radio"
              >
                <RadioIcon className="w-4 h-4" /> Host on Radio
              </Link>
            </div>
            </div>
          </div>
        </section>
      )}

      {/* TOP 30 */}
      {top30.length > 0 && (
        <section data-testid="radio-top30" className="max-w-7xl mx-auto px-5 sm:px-8 pt-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-3xl font-black flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-tv-accent" /> Top 30 — All Stations
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {top30.slice(0, 15).map((t, i) => (
              <TrackRow
                key={t.id}
                n={i + 1}
                track={t}
                onClick={() => playQueue(top30, i, { source: "top30" })}
              />
            ))}
          </div>
        </section>
      )}

      {/* FEATURED PLAYLISTS */}
      {playlists.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-14">
          <h2 className="font-display text-3xl font-black flex items-center gap-2 mb-4">
            <Music2 className="w-6 h-6 text-tv-accent" /> Featured Playlists
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {playlists.map((p) => (
              <Link
                key={p.id}
                data-testid={`radio-playlist-${p.slug}`}
                to={`/radio/playlist/${p.slug}`}
                className="group block"
              >
                <div
                  className="aspect-square rounded-xl overflow-hidden bg-white/5 border border-white/10 group-hover:border-tv-accent/60 transition"
                  style={{ backgroundImage: `url(${p.cover_url || ""})`, backgroundSize: "cover", backgroundPosition: "center" }}
                />
                <div className="mt-2 font-display font-black text-base line-clamp-1">{p.name}</div>
                <div className="text-white/50 text-xs line-clamp-1">{p.description}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* STATIONS — Search/filter + Favourites + main grid */}
      <section data-testid="radio-stations" className="max-w-7xl mx-auto px-5 sm:px-8 pt-14">
        {/* Search + filter row */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <h2 className="font-display text-3xl font-black flex items-center gap-2 mr-auto">
            <RadioIcon className="w-6 h-6 text-tv-accent" /> My Stations
          </h2>
          <div className="relative">
            <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="radio-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search stations…"
              className="bg-black/40 border border-white/10 rounded-full pl-9 pr-3 py-2 text-white text-sm focus:border-tv-accent outline-none w-56"
            />
          </div>
          <select
            data-testid="radio-category-filter"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="bg-black/40 border border-white/10 rounded-full px-4 py-2 text-white text-sm capitalize focus:border-tv-accent outline-none"
          >
            {categories.map((c) => (
              <option key={c} value={c} className="capitalize">{c === "all" ? "All categories" : c}</option>
            ))}
          </select>
        </div>

        {/* Favourites rail — only shown when the user has at least one fave. */}
        {favouriteStations.length > 0 && (
          <div className="mb-8" data-testid="radio-favourites-rail">
            <div className="flex items-center gap-2 mb-3">
              <Star className="w-4 h-4 text-tv-accent fill-tv-accent" />
              <h3 className="font-display text-xl font-black">Your Stations</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {favouriteStations.map((s) => (
                <StationCard
                  key={`fav-${s.id}`}
                  station={s}
                  liveSession={liveBySlug[s.slug]}
                  isFavourite={true}
                  onToggleFavourite={() => toggleFavourite(s.slug)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Main station grid */}
        {filteredStations.length === 0 && (q || category !== "all") ? (
          <div className="text-white/50 text-sm py-8" data-testid="radio-no-stations">
            No stations match {q ? `"${q}"` : ""}{category !== "all" ? ` in category ${category}` : ""}.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {filteredStations.map((s) => (
              <StationCard
                key={s.id}
                station={s}
                liveSession={liveBySlug[s.slug]}
                isFavourite={favouriteSlugs.has(s.slug)}
                onToggleFavourite={() => toggleFavourite(s.slug)}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * StationCard — station tile that (a) click-through to detail on the card
 * itself, AND (b) surfaces a dedicated PlayCircle icon that enqueues the
 * station's tracks straight into the dock without leaving /radio.
 */
function StationCard({ station, liveSession, isFavourite, onToggleFavourite }) {
  const { playQueue } = useRadioPlayer();
  const playNow = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const { data } = await api.get(`/radio/stations/${station.slug}`);
      playQueue(data?.tracks || [], 0, { source: `station:${station.slug}` });
    } catch {}
  };
  const np = liveSession?.now_playing;
  return (
    <Link
      data-testid={`radio-station-${station.slug}`}
      to={`/radio/station/${station.slug}`}
      className="group block rounded-xl overflow-hidden bg-gradient-to-br from-white/10 to-white/0 border border-white/10 hover:border-tv-accent/60 transition relative"
    >
      <div
        className={`aspect-square ${liveSession ? "ring-2 ring-red-500/70" : ""}`}
        style={{ backgroundImage: `url(${station.cover_url || ""})`, backgroundSize: "cover", backgroundPosition: "center" }}
      />
      {liveSession && (
        <div className="absolute left-3 top-3" data-testid={`radio-station-onair-${station.slug}`}>
          <LiveNowBadge
            variant="onair"
            hostName={liveSession.host_name}
            hostAvatar={liveSession.host_avatar}
            listeners={liveSession.listeners}
          />
        </div>
      )}
      {/* Favourite star — top-right of the artwork */}
      {onToggleFavourite && (
        <button
          data-testid={`radio-station-favourite-${station.slug}`}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFavourite(); }}
          aria-label={isFavourite ? "Unfavourite" : "Favourite"}
          title={isFavourite ? "Unfavourite" : "Favourite"}
          className={`absolute right-3 top-3 w-8 h-8 rounded-full backdrop-blur-sm flex items-center justify-center transition ${
            isFavourite ? "bg-tv-accent text-black" : "bg-black/50 text-white/80 hover:bg-black/70"
          }`}
        >
          <Star className={`w-4 h-4 ${isFavourite ? "fill-black" : ""}`} />
        </button>
      )}
      <button
        data-testid={`radio-station-play-${station.slug}`}
        onClick={playNow}
        title={`Play ${station.name}`}
        aria-label={`Play ${station.name}`}
        className="absolute right-3 bottom-[5.5rem] w-10 h-10 rounded-full bg-tv-accent text-black shadow-lg opacity-0 group-hover:opacity-100 focus:opacity-100 hover:scale-105 flex items-center justify-center transition"
      >
        <PlayCircle className="w-6 h-6" />
      </button>
      <div className="p-3">
        <div className="font-display font-black text-base line-clamp-1 group-hover:text-tv-accent transition">{station.name}</div>
        {/* Now-playing line wins over the static description when a host is live */}
        {liveSession ? (
          np ? (
            <div className="text-red-300 text-xs line-clamp-1 font-bold" data-testid={`radio-station-nowplaying-${station.slug}`}>
              ♫ {np.title}{np.artist ? ` — ${np.artist}` : ""}
            </div>
          ) : (
            <div className="text-red-300 text-xs line-clamp-1 font-bold">
              {liveSession.host_name} is on air now
            </div>
          )
        ) : (
          <div className="text-white/50 text-xs line-clamp-1">{station.description}</div>
        )}
        <div className="text-white/40 text-[10px] mt-1 uppercase tracking-wider">{station.track_count || 0} tracks</div>
      </div>
    </Link>
  );
}

function TrackRow({ n, track, onClick }) {
  return (
    <button
      data-testid={`radio-track-${track.id}`}
      onClick={onClick}
      className="flex items-center gap-3 bg-white/5 hover:bg-white/10 rounded-lg p-2 text-left group transition"
    >
      <div className="shrink-0 w-8 text-center font-black text-white/40 text-sm tabular-nums">{n}</div>
      <div
        className="shrink-0 w-12 h-12 rounded bg-white/10 overflow-hidden"
        style={{ backgroundImage: `url(${track.cover_url || ""})`, backgroundSize: "cover", backgroundPosition: "center" }}
      />
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm line-clamp-1 group-hover:text-tv-accent transition">{track.title}</div>
        <div className="text-white/55 text-xs line-clamp-1">{track.artist}</div>
      </div>
      <PlayCircle className="w-6 h-6 text-tv-accent opacity-0 group-hover:opacity-100 transition" />
    </button>
  );
}
